import React from 'react';
import { styled } from '@mui/system';
import { Box } from '@mui/material';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import FormatListNumberedOutlinedIcon from '@mui/icons-material/FormatListNumberedOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import PriorityHighOutlinedIcon from '@mui/icons-material/PriorityHighOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// Helper function to parse and display prefill values intelligently
const parsePrefillValue = prefillValue => {
  if (!prefillValue) return '';

  // If it's a string, check for placeholders
  if (typeof prefillValue === 'string') {
    // Check if it contains placeholders like ${placeholder.name}
    const placeholderMatches = prefillValue.match(/\$\{[^}]+\}/g);
    if (placeholderMatches) {
      // If it's just a single placeholder, show it cleanly
      if (prefillValue.trim() === placeholderMatches[0]) {
        return placeholderMatches[0];
      }
      // If it's mixed text with placeholders, show the whole string
      return prefillValue;
    }
    // Regular string value
    return prefillValue;
  }

  // If it's an object (conditional logic)
  if (typeof prefillValue === 'object') {
    try {
      // Try to extract meaningful info from conditional logic
      if (prefillValue.Statement?.content?.Condition) {
        const conditions = prefillValue.Statement.content.Condition;
        if (conditions.length > 0 && conditions[0].operators?.length > 0) {
          const firstCondition = conditions[0].operators[0];
          if (firstCondition.key && firstCondition.value) {
            return `If ${firstCondition.key} ${firstCondition.function?.toLowerCase() || '='} ${
              firstCondition.value
            }`;
          }
        }
      }
      return '[Conditional Logic]';
    } catch (e) {
      return '[Conditional Logic]';
    }
  }

  return String(prefillValue);
};

// Modern config badge styling inspired by shadcn
const ConfigBadge = styled(Box)(({ theme, variant = 'default' }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3px',
  padding: '1px 4px',
  borderRadius: '3px',
  fontSize: '10px',
  fontWeight: 500,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  marginRight: '3px',
  marginBottom: '2px',
  border: '1px solid',
  transition: 'all 0.2s ease-in-out',
  // Default muted appearance
  backgroundColor: theme.palette.action.hover,
  borderColor: theme.palette.divider,
  color: theme.palette.text.secondary,
  '& .MuiSvgIcon-root': {
    fontSize: '10px',
  },
  // No individual hover states - controlled by card hover
}));

const ConfigContainer = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '2px',
  marginTop: '4px',
  alignItems: 'flex-start',
  opacity: 1,
  pointerEvents: 'auto',
}));

/**
 * Shared FormCard configuration badges component
 * @param {Object} props
 * @param {Object} props.data - The form field configuration data
 * @param {number} props.fieldType - The field type number
 */
const FormCardConfig = ({ data, fieldType }) => {
  return (
    <>
      <ConfigContainer className="config-badges">
        {data.required && (
          <ConfigBadge className="variant-error">
            <PriorityHighOutlinedIcon />
            {typeof data.required === 'object' ? 'Required: Conditional' : 'Required'}
          </ConfigBadge>
        )}

        <ConfigBadge
          className={
            !data.visibility
              ? 'variant-error'
              : typeof data.visibility === 'object'
              ? 'variant-warning'
              : 'variant-success'
          }
        >
          {data.visibility ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
          {data.visibility
            ? typeof data.visibility === 'object'
              ? 'Visibility: Conditional'
              : 'Visible'
            : 'Hidden'}
        </ConfigBadge>

        {data.key && (
          <ConfigBadge className="variant-primary">
            <LabelOutlinedIcon />
            {`\${${data.key}.answer}`}
          </ConfigBadge>
        )}

        {data.readonly && (
          <ConfigBadge className="variant-warning">
            <LockOutlinedIcon />
            {typeof data.readonly === 'object' ? 'Read-only: Conditional' : 'Read-only'}
          </ConfigBadge>
        )}

        {data.autoAnswer && data.autoAnswer !== '' && (
          <ConfigBadge className="variant-primary">
            <SmartToyOutlinedIcon />
            {typeof data.autoAnswer === 'string'
              ? `Auto-answer: ${data.autoAnswer}`
              : 'Auto-answer: Conditional'}
          </ConfigBadge>
        )}

        {((data.validation?.min && data.validation.min !== '') ||
          (data.validation?.max && data.validation.max !== '') ||
          (data.validation?.regex && data.validation.regex !== '')) && (
          <ConfigBadge className="variant-success">
            <VerifiedUserOutlinedIcon />
            {[
              data.validation.min && data.validation.min !== '' && `Min: ${data.validation.min}`,
              data.validation.max && data.validation.max !== '' && `Max: ${data.validation.max}`,
              data.validation.regex && data.validation.regex !== '' && 'Regex',
            ]
              .filter(Boolean)
              .join(', ')}
          </ConfigBadge>
        )}

        {data.databaseId && data.columnId && (
          <ConfigBadge className="variant-primary">
            <StorageOutlinedIcon />
            Database
          </ConfigBadge>
        )}

        {(data.list_min > 0 || data.list_max > 0) && (
          <ConfigBadge className="variant-default">
            <FormatListNumberedOutlinedIcon />
            {[
              data.list_min > 0 && `Min: ${data.list_min}`,
              data.list_max > 0 && `Max: ${data.list_max}`,
            ]
              .filter(Boolean)
              .join(', ')}
          </ConfigBadge>
        )}

        {data.file_type && data.file_type !== 'pdf' && (
          <ConfigBadge className="variant-default">
            <AttachFileOutlinedIcon />
            {data.file_type.toUpperCase()}
          </ConfigBadge>
        )}

        {fieldType === 7 &&
          ((data.outputFormat && data.outputFormat !== '') ||
            (data.timezone && data.timezone !== '' && data.timezone !== 'UTC')) && (
            <ConfigBadge className="variant-default">
              <AccessTimeOutlinedIcon />
              {[
                data.outputFormat && data.outputFormat !== '' && data.outputFormat,
                data.timezone && data.timezone !== '' && data.timezone !== 'UTC' && data.timezone,
              ]
                .filter(Boolean)
                .join(', ')}
            </ConfigBadge>
          )}

        {data.notificationEmails?.shouldNotify && (
          <ConfigBadge className="variant-warning">
            <NotificationsOutlinedIcon />
            Notifications
          </ConfigBadge>
        )}
      </ConfigContainer>
      {data.required && !data.visibility && (
        <ConfigBadge
          className="variant-error"
          style={{ marginTop: '8px', width: '100%', justifyContent: 'flex-start' }}
        >
          <ErrorOutlineIcon />
          Configuration conflict: Required field is hidden
        </ConfigBadge>
      )}
    </>
  );
};

export default FormCardConfig;
export { parsePrefillValue };
